import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, TrendingUp, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Button } from "@/components/ui/button";

const reasons = [
  {
    icon: TrendingUp,
    title: "New revenue streams",
    body:
      "Launch premium digital products for your list — from interactive portals to advisory tools — built on top of the books you already publish.",
  },
  {
    icon: Users,
    title: "Author success",
    body:
      "Give your authors a proven platform to sell consulting, courses, workshops and retainers, without your team building new technology.",
  },
  {
    icon: BookOpen,
    title: "Increased book value",
    body:
      "Extend the life of every title by linking each book to an interactive experience that drives ongoing engagement, data and follow-on sales.",
  },
  {
    icon: Sparkles,
    title: "Competitive edge",
    body:
      "Offer a differentiated author package other houses cannot match, with AI-powered services that make your list stand out to agents and authors.",
  },
];

const benefits = [
  {
    title: "New Revenue Streams",
    body:
      "Turn existing titles into premium digital products, workshops, advisory tools and interactive experiences that generate long-tail revenue.",
  },
  {
    title: "Author Success",
    body:
      "Give your authors a ready-to-launch platform to teach, coach and engage their audiences, without requiring your team to build new technology.",
  },
  {
    title: "Extend Book Value",
    body:
      "Increase the lifespan and impact of every title by linking it to a learning experience that drives deeper engagement and additional sales.",
  },
  {
    title: "Competitive Edge",
    body:
      "Offer a modern author package that strengthens your list, attracts top talent and differentiates your publishing house in a crowded market.",
  },
];

const Publishers = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        eyebrow="For publishers and imprints"
        title={
          <>
            Publish the next generation of{" "}
            <span className="text-gold-gradient">author experiences.</span>
          </>
        }
        intro="Partner with AuthorPro to turn your authors' expertise into digital products — books become interactive platforms, and your house unlocks recurring revenue from titles you have already published."
      >
        <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold px-8">
          <a
            href="https://meetings.series-a.co/meetings/series-a/authorpro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            Partner With Us
            <ArrowRight className="h-4 w-4" />
          </a>
        </Button>
        <Button asChild size="lg" variant="outline" className="px-8">
          <Link to="/#case-studies">View Case Studies</Link>
        </Button>
      </PageHero>

      {/* Why partner */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">Why partner with AuthorPro</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1] mb-5">
              White-label platforms for the authors you already publish.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We work with publishers to build AI-powered platforms under your brand, turning your list
              into interactive business products that generate shared revenue.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {reasons.map(({ icon: Icon, title, body }) => (
              <article key={title} className="card-noir-hover p-7">
                <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-sm border border-border bg-background text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold mb-3">{title}</h3>
                <p className="text-[15px] text-muted-foreground leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership benefits */}
      <section className="py-20 md:py-28 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">Partnership benefits</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              What a partnership delivers.
            </h2>
          </div>

          <div className="max-w-4xl">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                className="grid gap-4 border-t border-border py-8 md:grid-cols-[auto_1fr_1.4fr] md:gap-10 md:items-baseline last:border-b"
              >
                <span className="font-serif italic text-gold text-sm tabular-nums">0{i + 1}</span>
                <h3 className="font-serif text-2xl leading-snug">{b.title}</h3>
                <p className="text-[15px] md:text-base text-muted-foreground leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      <CTASection />
      <Footer />
    </div>
  );
};

export default Publishers;
